import { useMutation, useQuery, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import { useMemo } from 'react';
import { toast } from 'sonner';

import { Client, GlobalAccount } from '@ndvr/customer-api';
import {
    Account,
    AccountAllocationUpdateRequest,
    AccountCashEqSweepUpdateRequest,
    AccountGroup,
    AccountSaveRequest,
    AccountTaxLossHarvestingUpdateRequest,
    BatchOnboardRequest,
    BatchOnboardResponse,
    CustodyAccountLinkRequest,
    GUID,
    LifecycleStatus,
    LifestyleProtectionUpdateRequest,
    ReferencePortfolio,
    TaxBudgetUpdateRequest,
    Tenant,
} from '@ndvr/portfolio-api';

import { forgetRecentSearchEntity } from '@/components/recent-search-store';
import { PortfolioApi } from 'API';
import { Toast } from 'widgets';

import { useSelectedTenantGUIDs } from '../auth/tenant-selection';

export const useAccount = (accountGUID?: GUID<Account>, options?: Partial<UseQueryOptions<Account | undefined>>) => {
    return useQuery({
        ...options,
        queryKey: ['account', accountGUID],
        queryFn: async () => {
            const [response] = await PortfolioApi().getAccount(accountGUID!);
            return response ?? undefined;
        },
        enabled: !!accountGUID,
    });
};

export const useCreateAccount = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            accountGroupGUID,
            request,
        }: {
            accountGroupGUID: GUID<AccountGroup>;
            request: AccountSaveRequest;
        }) => {
            const [response] = await PortfolioApi().createAccount(accountGroupGUID, request);
            return response ?? undefined;
        },
        onSuccess: (_, { accountGroupGUID }) => {
            toast.success('Account created successfully.');
            queryClient.invalidateQueries({
                queryKey: ['accountGroup', accountGroupGUID],
            });
            queryClient.invalidateQueries({
                queryKey: ['globalAccount'],
            });
        },
        onError: (error) => Toast.forError(error, 'Failed to create account'),
    });
};

export const useAccountGroupGUIDForAccount = (accountGUID?: GUID<Account>) => {
    return useQuery({
        queryKey: ['account', accountGUID, 'group'],
        queryFn: async () => {
            const [response] = await PortfolioApi().getAccountGroupGUIDForAccount(accountGUID!);
            return response ?? undefined;
        },
        enabled: !!accountGUID,
    });
};

export const useGetMonthlyCashFlow = (accountGroupGUID?: GUID<AccountGroup>, includeDrafts?: boolean) => {
    return useQuery({
        queryKey: ['accountGroup', accountGroupGUID, 'monthlyCashFlow', includeDrafts],
        queryFn: async () => {
            const [response] = await PortfolioApi().getMonthlyCashFlow(accountGroupGUID!, {
                includeDrafts: includeDrafts || false,
            });
            return response ?? undefined;
        },
        enabled: !!accountGroupGUID,
    });
};

export const useUpdateAccount = (accountGUID: GUID<Account>, accountGroupGUID: GUID<AccountGroup>) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (request: AccountSaveRequest) => {
            return await PortfolioApi().updateAccount(accountGUID, request);
        },
        onSuccess: () => {
            toast.success('Account Updated');
            queryClient.invalidateQueries({
                queryKey: ['accountGroup', accountGroupGUID],
            });
            queryClient.invalidateQueries({
                queryKey: ['account', accountGUID],
            });
        },
    });
};

export const useUpdateAccountAllocation = (accountGUID: GUID<Account>, accountGroupGUID: GUID<AccountGroup>) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (request: AccountAllocationUpdateRequest) => {
            return await PortfolioApi().updateAccountAllocation(accountGUID, request);
        },
        onSuccess: () => {
            toast.success('Account allocation updated');
            queryClient.invalidateQueries({ queryKey: ['accountGroup', accountGroupGUID] });
            queryClient.invalidateQueries({ queryKey: ['account', accountGUID] });
            queryClient.invalidateQueries({ queryKey: ['optimization', 'allocation', accountGroupGUID] });
        },
        onError: (error) => Toast.forError(error, 'Failed to update account allocation'),
    });
};

export const useUpdateCashEqSweep = (accountGUID: GUID<Account>, accountGroupGUID: GUID<AccountGroup>) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (request: AccountCashEqSweepUpdateRequest) => {
            return await PortfolioApi().updateCashEqSweep(accountGUID, request);
        },
        onSuccess: () => {
            toast.success('CashEQ sweep updated');
            queryClient.invalidateQueries({ queryKey: ['accountGroup', accountGroupGUID] });
            queryClient.invalidateQueries({ queryKey: ['account', accountGUID] });
        },
        onError: (error) => Toast.forError(error, 'Failed to update CashEQ sweep'),
    });
};

export const useUpdateTaxLossHarvesting = (accountGUID: GUID<Account>, accountGroupGUID: GUID<AccountGroup>) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (request: AccountTaxLossHarvestingUpdateRequest) => {
            return await PortfolioApi().updateTaxLossHarvesting(accountGUID, request);
        },
        onSuccess: () => {
            toast.success('Tax loss harvesting updated');
            queryClient.invalidateQueries({ queryKey: ['accountGroup', accountGroupGUID] });
            queryClient.invalidateQueries({ queryKey: ['account', accountGUID] });
        },
        onError: (error) => Toast.forError(error, 'Failed to update tax loss harvesting'),
    });
};

export const useUpdateTaxBudget = (accountGUID: GUID<Account>, accountGroupGUID: GUID<AccountGroup>) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (request: TaxBudgetUpdateRequest) => {
            return await PortfolioApi().updateTaxBudget(accountGUID, request);
        },
        onSuccess: () => {
            toast.success('Tax Budget Updated');
            queryClient.invalidateQueries({ queryKey: ['accountGroup', accountGroupGUID] });
            queryClient.invalidateQueries({ queryKey: ['account', accountGUID] });
            queryClient.invalidateQueries({ queryKey: ['taxMetrics', 'ytd'] });
        },
    });
};

export const useLinkAccountToCustodyAccount = (householdGUID: GUID<GlobalAccount>) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({
            accountGUID,
            request,
        }: {
            accountGroupGUID: GUID<AccountGroup>;
            accountGUID: GUID<Account>;
            request: CustodyAccountLinkRequest;
        }) => {
            return PortfolioApi().linkAccountToCustodyAccount(accountGUID, request);
        },
        onSuccess: (_, { accountGroupGUID, accountGUID }) => {
            Toast.success('Custody account linked');
            queryClient.invalidateQueries({
                queryKey: ['globalAccount', householdGUID],
            });
            queryClient.invalidateQueries({
                queryKey: ['accountGroup', accountGroupGUID],
            });
            queryClient.invalidateQueries({
                queryKey: ['account', accountGUID],
            });
            queryClient.invalidateQueries({ queryKey: ['accounts', 'portfolio-engine'] });
        },
    });
};

export const useUpdateAccountOwner = (householdGUID: GUID<GlobalAccount>) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({
            accountGUID,
            clientGUID,
        }: {
            accountGroupGUID: GUID<AccountGroup>;
            accountGUID: GUID<Account>;
            clientGUID: GUID<Client>;
        }) => {
            return PortfolioApi().updateAccountOwner(accountGUID, { clientGUID });
        },
        onSuccess: (_data, { accountGroupGUID, accountGUID }) => {
            Toast.success('Account owner updated');
            queryClient.invalidateQueries({
                queryKey: ['globalAccount', householdGUID],
            });
            queryClient.invalidateQueries({
                queryKey: ['accountGroup', accountGroupGUID],
            });
            queryClient.invalidateQueries({
                queryKey: ['account', accountGUID],
            });
            queryClient.invalidateQueries({ queryKey: ['accounts', 'portfolio-engine'] });
        },
        onError: (error) => Toast.forError(error, 'Failed to update account owner'),
    });
};

export const useCloseAccount = (householdGUID: GUID<GlobalAccount>) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ accountGUID }: { accountGroupGUID: GUID<AccountGroup>; accountGUID: GUID<Account> }) => {
            const [response] = await PortfolioApi().closeAccount(accountGUID);
            return response ?? undefined;
        },
        onSuccess: (_, { accountGroupGUID, accountGUID }) => {
            queryClient.invalidateQueries({
                queryKey: ['globalAccount', householdGUID],
            });
            queryClient.invalidateQueries({
                queryKey: ['accountGroup', accountGroupGUID],
            });
            queryClient.invalidateQueries({
                queryKey: ['account', accountGUID],
            });
            queryClient.invalidateQueries({ queryKey: ['accounts', 'portfolio-engine'] });
        },
    });
};

export const useDeleteAccount = (householdGUID: GUID<GlobalAccount>) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ accountGUID }: { accountGroupGUID: GUID<AccountGroup>; accountGUID: GUID<Account> }) => {
            const [response] = await PortfolioApi().deleteAccount(accountGUID);
            return response ?? undefined;
        },
        onSuccess: (result, { accountGroupGUID, accountGUID }) => {
            if (result?.valid === true) {
                forgetRecentSearchEntity(accountGUID);
            }
            queryClient.invalidateQueries({
                queryKey: ['globalAccount', householdGUID],
            });
            queryClient.invalidateQueries({
                queryKey: ['accountGroup', accountGroupGUID],
            });
            queryClient.invalidateQueries({ queryKey: ['accounts', 'portfolio-engine'] });
        },
        onError: (error) => Toast.forError(error, 'Failed to delete account'),
    });
};

export const useListAccountsByLifecycleStatus = (lifecycleStatus: LifecycleStatus[]) => {
    return useQuery({
        queryKey: ['account'],
        queryFn: async () => {
            const [response] = await PortfolioApi().listAccountsByLifecycleStatus({ lifecycleStatus });
            return response ?? [];
        },
    });
};

export const useSuspendedAccounts = () => {
    const accountsQuery = useListAccountsForTenant();

    const data = useMemo(
        () =>
            (accountsQuery.data ?? []).filter(
                (account) =>
                    account.tradingSuspendedAt &&
                    account.lifecycleStatus === LifecycleStatus.REAL &&
                    account.referencePortfolio !== ReferencePortfolio.CLIENT_DIRECTED &&
                    account.referencePortfolio !== ReferencePortfolio.EXTERNALLY_MANAGED,
            ),
        [accountsQuery.data],
    );

    return {
        ...accountsQuery,
        data,
    };
};

export const useListAccountsForTenant = () => {
    const tenantGUIDs = useSelectedTenantGUIDs();

    return useQuery({
        queryKey: ['accounts', 'all', 'tenants', tenantGUIDs],
        queryFn: async () => {
            const [response] = await PortfolioApi().listAccountsForTenants(tenantGUIDs);
            return response ?? [];
        },
        enabled: tenantGUIDs.length > 0,
    });
};

export const useAccountsForTenant = (tenantGUID: GUID<Tenant>) =>
    useQuery({
        queryKey: ['accounts', 'all', 'tenant', tenantGUID],
        queryFn: async () => {
            const [response] = await PortfolioApi().listAccountsForTenant(tenantGUID);
            return response ?? [];
        },
    });

export const useListPortfolioEngineAccountsForTenants = (lifecycleStatus?: LifecycleStatus) => {
    const tenantGUIDs = useSelectedTenantGUIDs();

    return useQuery({
        queryKey: ['accounts', 'portfolio-engine', 'tenants', tenantGUIDs, lifecycleStatus],
        queryFn: async () => {
            const [response] = await PortfolioApi().listPortfolioEngineAccountsForTenants(
                tenantGUIDs,
                lifecycleStatus ? { lifecycleStatus } : undefined,
            );
            return response ?? [];
        },
        enabled: tenantGUIDs.length > 0,
    });
};

export const useListPortfolioEngineAccountsForHousehold = (
    householdGUID: GUID<GlobalAccount>,
    lifecycleStatus?: LifecycleStatus,
    options?: { throwOnError?: boolean },
) =>
    useQuery({
        ...options,
        queryKey: ['accounts', 'portfolio-engine', 'household', householdGUID, lifecycleStatus],
        queryFn: async () => {
            const [response] = await PortfolioApi().listPortfolioEngineAccountsForHousehold(
                householdGUID,
                lifecycleStatus ? { lifecycleStatus } : undefined,
            );
            return response ?? [];
        },
    });

// Batch account name lookup by GUID — for callers that already know exactly which
// accounts they need (e.g. resolving names for a specific run's accounts on expansion)
// rather than pulling every account for the selected tenant(s).
export const useAccountLookup = (accountGUIDs: GUID<Account>[]) => {
    const sortedGUIDs = useMemo(() => [...accountGUIDs].sort(), [accountGUIDs]);

    return useQuery({
        queryKey: ['accounts', 'lookup', sortedGUIDs],
        queryFn: async () => {
            const [response] = await PortfolioApi().listAccountLookup(sortedGUIDs);
            return response ?? [];
        },
        enabled: sortedGUIDs.length > 0,
    });
};

export const useBatchOnboard = (
    tenantGUID: GUID<Tenant> | undefined,
    { onSuccess }: { onSuccess?: (response: BatchOnboardResponse) => void } = {},
) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (request: BatchOnboardRequest) => {
            if (!tenantGUID) {
                throw new Error('No tenant selected');
            }

            const [response] = await PortfolioApi().onboard(tenantGUID, request);
            return response;
        },
        onSuccess: (response) => {
            const onboardedCount = response?.onboardedAccounts.length ?? 0;
            toast.success(onboardedCount === 1 ? 'Account onboarded' : `${onboardedCount} accounts onboarded`);

            if (response?.warnings.length) {
                toast.warning(response.warnings.join('\n'));
            }

            queryClient.invalidateQueries({ queryKey: ['accounts', 'portfolio-engine'] });
            queryClient.invalidateQueries({ queryKey: ['accountGroup'] });
            queryClient.invalidateQueries({ queryKey: ['globalAccount'] });

            if (response) onSuccess?.(response);
        },
        onError: (error) => Toast.forError(error, 'Failed to onboard accounts'),
    });
};

export const useUpdateAccountLifestyleProtection = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            accountGuid,
            lifestyleProtectionRequest,
        }: {
            accountGuid: GUID<Account>;
            lifestyleProtectionRequest: LifestyleProtectionUpdateRequest;
        }) => {
            const [response] = await PortfolioApi().updateLifestyleProtection(accountGuid, lifestyleProtectionRequest);
            return response ?? undefined;
        },
        onSuccess: () => {
            toast.success('Lifestyle protection updated');
            queryClient.invalidateQueries({
                queryKey: ['accountGroup'],
            });
            queryClient.invalidateQueries({
                queryKey: ['optimization', 'allocation'],
            });
        },
        onError: (error) => Toast.forError(error, 'Failed to update lifestyle protection'),
    });
};

export const useMoveAccount = (householdGUID: GUID<GlobalAccount>) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            accountGUID,
            targetAccountGroupGUID,
        }: {
            accountGUID: GUID<Account>;
            targetAccountGroupGUID: GUID<AccountGroup>;
        }) => {
            const [response] = await PortfolioApi().moveAccount(accountGUID, targetAccountGroupGUID);
            return response ?? undefined;
        },
        onSuccess: () => {
            toast.success('Account moved successfully');
            queryClient.invalidateQueries({
                queryKey: ['globalAccount', householdGUID],
            });
            queryClient.invalidateQueries({
                queryKey: ['accountGroup'],
            });
        },
        onError: (error) => Toast.forError(error, 'Failed to move account'),
    });
};

export const useBulkSetAccountsTradeStatus = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ accountGUIDs, action }: { accountGUIDs: GUID<Account>[]; action: 'suspend' | 'enable' }) =>
            action === 'suspend'
                ? PortfolioApi().suspendTradingForAccounts(accountGUIDs)
                : PortfolioApi().enableTradingForAccounts(accountGUIDs),
        onSuccess: (_, { accountGUIDs, action }) => {
            const verb = action === 'suspend' ? 'suspended' : 'enabled';
            toast.success(
                accountGUIDs.length === 1 ? `Trading ${verb}` : `Trading ${verb} for ${accountGUIDs.length} accounts`,
            );
            queryClient.invalidateQueries({ queryKey: ['globalAccount'] });
            queryClient.invalidateQueries({ queryKey: ['accountGroup'] });
            queryClient.invalidateQueries({ queryKey: ['accounts', 'portfolio-engine'] });
        },
        onError: (error, { action }) => {
            Toast.forError(error, `Failed to ${action === 'suspend' ? 'suspend' : 'enable'} trading`);
        },
    });
};
